import { BotIcon, CableIcon, SparklesIcon } from "lucide-react";
import { createAvatar } from "@dicebear/core";
import * as shapes from "@dicebear/shapes";
import type { CmmdProjectAgentMember } from "@cmmd-forge/contracts";

import { ClaudeAI, CursorIcon, OpenAI } from "~/components/Icons";
import { cn } from "~/lib/utils";

const PROVIDER_LABELS: Readonly<Record<CmmdProjectAgentMember["agentKind"], string>> = {
  forge: "Forge",
  codex: "Codex",
  claude: "Claude",
  cursor: "Cursor",
  integration: "Integration",
  custom: "Custom",
};

export function getAgentMemberAvatarSeed(member: CmmdProjectAgentMember): string | null {
  if (member.agentKind !== "forge") return null;
  const avatar = member.roleProfile["avatar"];
  if (typeof avatar !== "object" || avatar === null) return null;
  const record = avatar as Record<string, unknown>;
  return record["kind"] === "generated" && typeof record["seed"] === "string"
    ? record["seed"]
    : null;
}

export function generatedAgentAvatarUrl(seed: string): string {
  return createAvatar(shapes, {
    seed,
    backgroundColor: ["18181b"],
    shape1Color: ["f97316"],
    shape2Color: ["fb923c"],
    shape3Color: ["fed7aa"],
  }).toDataUri();
}

function AgentProviderIcon(props: {
  readonly kind: CmmdProjectAgentMember["agentKind"];
  readonly className?: string;
}) {
  if (props.kind === "codex") return <OpenAI className={props.className} aria-hidden />;
  if (props.kind === "claude") return <ClaudeAI className={props.className} aria-hidden />;
  if (props.kind === "cursor") return <CursorIcon className={props.className} aria-hidden />;
  if (props.kind === "forge") {
    return <img src="/forge-mark.svg" className={props.className} alt="" aria-hidden />;
  }
  if (props.kind === "integration") return <CableIcon className={props.className} aria-hidden />;
  return <SparklesIcon className={props.className} aria-hidden />;
}

export function AgentMemberAvatar(props: {
  readonly member: CmmdProjectAgentMember;
  readonly className?: string;
}) {
  const avatarSeed = getAgentMemberAvatarSeed(props.member);
  return (
    <span
      className={cn(
        "flex size-9 shrink-0 items-center justify-center rounded-md border border-border bg-muted text-muted-foreground",
        props.className,
      )}
    >
      {avatarSeed ? (
        <img
          src={generatedAgentAvatarUrl(avatarSeed)}
          className="size-full rounded-[inherit] object-cover"
          alt=""
          aria-hidden
        />
      ) : (
        <AgentProviderIcon kind={props.member.agentKind} className="size-4.5 object-contain" />
      )}
      <span className="sr-only">
        {props.member.displayName}, {PROVIDER_LABELS[props.member.agentKind]} Agent Member
      </span>
    </span>
  );
}

export function AgentKindBadge(props: { readonly kind: CmmdProjectAgentMember["agentKind"] }) {
  return (
    <span className="inline-flex items-center gap-1 rounded border border-border bg-muted px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
      <AgentProviderIcon kind={props.kind} className="size-2.5 object-contain" />
      {PROVIDER_LABELS[props.kind]}
    </span>
  );
}

export function AgentIdentityBadge() {
  return (
    <span className="inline-flex items-center gap-1 rounded border border-border px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
      <BotIcon className="size-2.5" aria-hidden />
      Agent Member
    </span>
  );
}
